import type { Tenant } from "~/domain/types";

/**
 * The only tenant this deployment serves.
 *
 * `POST /session` still requires `tenant_id`. This deployment is single-tenant,
 * so the container injects that id instead of asking the person signing in to
 * type a UUID. Keep this string in lockstep with
 * `config/provisioning/tenant.json` `tenant.id` — `bootstrap-tenant.test.ts`
 * fails if they diverge.
 */
export const BOOTSTRAP_TENANT_ID = "00000000-0000-4000-8000-00000000f001";

/**
 * Form values seeded from a bootstrap tenant.
 *
 * The mapper is tenant-shaped, not deployment-shaped: a bootstrap tenancy
 * keeps its name, postal address and timezone. Nothing here rewrites those
 * to the provisioned VeryBooked / UTC defaults.
 */
export interface WorkspaceProfileFromTenant {
  name: string;
  physicalAddress: string;
  timezone: string;
}

/**
 * Map a bootstrap tenant onto the workspace profile form.
 *
 * An unset postal address is an empty field, not the string "not set". A
 * footer that reads "not set" looks answered; an empty field reads as the
 * compliance gap it is. The timezone is passed through — a Dublin tenant
 * must not be left on UTC because this file once defaulted the zone.
 */
export function workspaceProfileFromTenant(
  tenant: Pick<Tenant, "name" | "timezone"> & { physicalAddress?: Tenant["physicalAddress"] },
): WorkspaceProfileFromTenant {
  return {
    name: tenant.name,
    physicalAddress: tenant.physicalAddress ?? "",
    timezone: tenant.timezone,
  };
}
